import { ColorSchemeToggle, useLanguageAwareRouting } from '@gofranz/common-components';
import {
  Anchor,
  Box,
  Burger,
  Button,
  Container,
  Drawer,
  Group,
  Image,
  Menu,
  Stack,
  Text,
  UnstyledButton,
  useComputedColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import type { CSSProperties, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { LanguageSelector } from '../LanguageSelector/LanguageSelector';

interface NavLinkProps {
  to: string;
  active: boolean;
  isDark: boolean;
  children: ReactNode;
}

function NavLink({ to, active, isDark, children }: NavLinkProps) {
  const baseColor = isDark ? '#c5c6cd' : '#44474d';
  const accentColor = isDark ? '#ffffff' : '#0A192F';

  const style: CSSProperties = {
    fontSize: 14,
    fontWeight: 500,
    color: active ? accentColor : baseColor,
    textDecoration: 'none',
    paddingBottom: 4,
    borderBottom: `2px solid ${active ? accentColor : 'transparent'}`,
    transition: 'color 150ms ease, border-color 150ms ease',
  };

  return (
    <Anchor
      component={Link}
      to={to}
      style={style}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLAnchorElement).style.color = accentColor;
      }}
      onMouseLeave={(e) => {
        if (!active) {
          (e.currentTarget as HTMLAnchorElement).style.color = baseColor;
        }
      }}
    >
      {children}
    </Anchor>
  );
}

export function PublicNavigation() {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const computedColorScheme = useComputedColorScheme('light');
  const { createLanguageURL, isActive } = useLanguageAwareRouting();
  const [drawerOpened, { toggle: toggleDrawer, close: closeDrawer }] = useDisclosure(false);

  const isDark = computedColorScheme === 'dark';
  const surfaceColor = isDark ? theme.colors.dark[7] : '#ffffff';
  const borderColor = isDark ? theme.colors.dark[5] : '#e4e8f0';
  const moreActive = isActive('/docs') || isActive('/news');

  return (
    <Box
      component="nav"
      style={{
        backgroundColor: surfaceColor,
        borderBottom: `1px solid ${borderColor}`,
      }}
    >
      <Container size="xl" h={64}>
        <Group justify="space-between" h="100%" wrap="nowrap" gap="md">
          <Group gap="xl" wrap="nowrap">
            <Anchor
              component={Link}
              to={createLanguageURL('/')}
              style={{
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <Image src="/logo.svg" alt={t('brand.logoAlt')} w={28} h={28} />
              <Text
                fw={700}
                size="lg"
                style={{
                  letterSpacing: '-0.01em',
                  color: isDark ? '#ffffff' : '#0A192F',
                }}
                visibleFrom="xs"
              >
                {t('brand.name')}
              </Text>
            </Anchor>
            <Group gap="lg" visibleFrom="sm" wrap="nowrap">
              <NavLink to={createLanguageURL('/')} active={isActive('/')} isDark={isDark}>
                {t('glob_navigation.home')}
              </NavLink>
              <NavLink
                to={createLanguageURL('/integrations')}
                active={isActive('/integrations')}
                isDark={isDark}
              >
                {t('glob_navigation.integrations')}
              </NavLink>
              <NavLink
                to={createLanguageURL('/pricing')}
                active={isActive('/pricing')}
                isDark={isDark}
              >
                {t('glob_navigation.pricing')}
              </NavLink>
              <Menu trigger="hover" openDelay={100} closeDelay={400}>
                <Menu.Target>
                  <UnstyledButton
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: moreActive
                        ? isDark
                          ? '#ffffff'
                          : '#0A192F'
                        : isDark
                          ? '#c5c6cd'
                          : '#44474d',
                      paddingBottom: 4,
                      borderBottom: `2px solid ${
                        moreActive ? (isDark ? '#ffffff' : '#0A192F') : 'transparent'
                      }`,
                      transition: 'color 150ms ease, border-color 150ms ease',
                    }}
                  >
                    {t('glob_navigation.more')}
                  </UnstyledButton>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item component={Link} to={createLanguageURL('/docs')}>
                    {t('glob_navigation.docs')}
                  </Menu.Item>
                  <Menu.Item component={Link} to={createLanguageURL('/news')}>
                    {t('glob_navigation.news')}
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
          </Group>
          <Group gap="md" wrap="nowrap">
            <LanguageSelector />
            <ColorSchemeToggle />
            <Group gap="md" visibleFrom="sm" wrap="nowrap">
              <Anchor
                component={Link}
                to={createLanguageURL('/login')}
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: isDark ? '#c5c6cd' : '#44474d',
                  textDecoration: 'none',
                }}
              >
                {t('glob_navigation.login')}
              </Anchor>
              <Button
                component={Link}
                to={createLanguageURL('/signup')}
                size="sm"
                radius="md"
                styles={{
                  root: {
                    backgroundColor: '#0A192F',
                    color: '#ffffff',
                    height: 36,
                    paddingInline: 16,
                    fontWeight: 500,
                    boxShadow: '0 1px 2px rgba(10, 25, 47, 0.08)',
                  },
                }}
              >
                {t('glob_navigation.getStarted')}
              </Button>
            </Group>
            <Burger opened={drawerOpened} onClick={toggleDrawer} hiddenFrom="sm" size="sm" />
          </Group>
        </Group>
      </Container>

      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        title={t('brand.name')}
        hiddenFrom="sm"
        zIndex={1000000}
      >
        <Stack gap="md">
          <Button
            component={Link}
            to={createLanguageURL('/')}
            variant={isActive('/') ? 'filled' : 'subtle'}
            fullWidth
            onClick={closeDrawer}
          >
            {t('glob_navigation.home')}
          </Button>
          <Button
            component={Link}
            to={createLanguageURL('/integrations')}
            variant={isActive('/integrations') ? 'filled' : 'subtle'}
            fullWidth
            onClick={closeDrawer}
          >
            {t('glob_navigation.integrations')}
          </Button>
          <Button
            component={Link}
            to={createLanguageURL('/pricing')}
            variant={isActive('/pricing') ? 'filled' : 'subtle'}
            fullWidth
            onClick={closeDrawer}
          >
            {t('glob_navigation.pricing')}
          </Button>
          <Button
            component={Link}
            to={createLanguageURL('/docs')}
            variant={isActive('/docs') ? 'filled' : 'subtle'}
            fullWidth
            onClick={closeDrawer}
          >
            {t('glob_navigation.docs')}
          </Button>
          <Button
            component={Link}
            to={createLanguageURL('/news')}
            variant={isActive('/news') ? 'filled' : 'subtle'}
            fullWidth
            onClick={closeDrawer}
          >
            {t('glob_navigation.news')}
          </Button>
          <Box
            mt="md"
            pt="md"
            style={{
              borderTop: `1px solid ${isDark ? theme.colors.dark[4] : theme.colors.gray[2]}`,
            }}
          >
            <Stack gap="sm">
              <Button
                component={Link}
                to={createLanguageURL('/login')}
                variant="outline"
                fullWidth
                onClick={closeDrawer}
              >
                {t('glob_navigation.login')}
              </Button>
              <Button
                component={Link}
                to={createLanguageURL('/signup')}
                variant="filled"
                fullWidth
                onClick={closeDrawer}
              >
                {t('glob_navigation.getStarted')}
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Drawer>
    </Box>
  );
}
